import { SettingsActionOutcome } from './settings-action-outcome';
import { SETTINGS_ACTION_TYPES } from './settings-actions-copy';
import { Button } from '@/components/ui/button';
import { useOptionalWorkspace } from '@/workspace/context';
import { WorkspaceToolCard } from '@/workspace/tool-cards';
import { AppEventCard, isAppWebhookOutcome } from '@/workspace/app-event-card';
import { SettingsChangeCard } from '@/workspace/settings-change-card';
import type { ActionView } from './action-confirmation';
import { actionResultTarget } from './action-result-target';
import { useActionSummary } from './action-approval-copy';

function artifact(result: unknown) {
  if (!result || typeof result !== 'object') return null;
  for (const kind of ['result', 'skill', 'agent'] as const) {
    const value = (result as Record<string, unknown>)[kind];
    if (!value || typeof value !== 'object') continue;
    const item = value as {
      id?: unknown;
      title?: unknown;
      name?: unknown;
      draft?: { name?: unknown };
    };
    if (typeof item.id !== 'string') continue;
    const title = item.title ?? item.name ?? item.draft?.name;
    return { kind, id: item.id, title: typeof title === 'string' ? title : null };
  }
  return null;
}

/** A schedule switch's outcome: the state it was left in and its next runs, as the switch returned them. */
function AppScheduleOutcome({ action }: { action: ActionView }) {
  const summary = useActionSummary()(action);
  return action.status === 'executed' ? <p data-testid="app-schedule-outcome">{summary}</p> : null;
}

/** Approval receipts link to the existing artifact canvas without duplicating its body. */
export function ActionOutcome({ action, openLabel }: { action: ActionView; openLabel: string }) {
  const workspace = useOptionalWorkspace();
  if (action.tool === 'update_my_settings')
    return <SettingsChangeCard output={action.result} toolCallId={action.id} />;
  // A webhook that is on: its URL, and its signing secret revealed only to the owner. Any other
  // switch is fully said by the row's sentence.
  if (action.tool === 'set_app_event') {
    const outcome = (action.result as { appEvent?: unknown } | null)?.appEvent;
    return isAppWebhookOutcome(outcome) ? <AppEventCard outcome={outcome} /> : null;
  }
  if (action.tool === 'set_app_schedule') return <AppScheduleOutcome action={action} />;
  if (Object.hasOwn(SETTINGS_ACTION_TYPES, action.tool))
    return <SettingsActionOutcome action={action} openLabel={openLabel} />;
  const destination = actionResultTarget(action);
  const target = artifact(action.result);
  if (destination || target)
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => workspace?.openCanvas(destination ?? { kind: target!.kind, id: target!.id })}
      >
        {openLabel}
        {target?.title ? ` · ${target.title}` : ''}
      </Button>
    );
  return <WorkspaceToolCard toolName={action.tool} toolCallId={action.id} result={action.result} />;
}
